import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ICLientData, IUser } from './shared/models';
import {
  AuthService, CookieService, EventsService, PortfolioService, ScriptLoaderService, SeoService, StorageService, UtilService, VariablesService
} from './shared/services';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  loadingRouteConfig: boolean;
  public result;
  deferredPrompt: any;
  showButton = true;
  params;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public titleService: Title,
    public authService: AuthService,
    public utilService: UtilService,
    public storageService: StorageService,
    public variablesService: VariablesService,
    public scriptLoaderService: ScriptLoaderService,
    public seoService: SeoService,
    private events: EventsService,
    private portfolioService: PortfolioService,
    private cookie: CookieService
  ) { }

  ngOnInit(): void {
    this.params = this.utilService.getUrlParams();
    this.moduleLoader();
    this.seoService.createCanonicalURL();
    const webPageSchema = {
      '@context': 'http://schema.org',
      '@id': 'https://www.ketto.org',
      '@type': 'WebPage',
      'url': 'https://www.ketto.org',
      'name': 'Ketto - Crowdfunding India'
    };
    this.seoService.schemaOrgObject(webPageSchema);
    if (this.utilService.isBrowser) {
      this.setDeviceType();
      this.routeChangeListner();
      this.getClientIP();
      this.setUser();
    }
    this.setTitle();
    this.variablesService.listenToGlabalJsVar();
  }

  setDeviceType() {
    this.variablesService.deviceType = this.utilService.isMobile() ? 'mobile' : 'desktop';
    const platform = this.cookie.getCookie('platform');
    if (platform) {
      this.variablesService.deviceType = platform;
    }
    if (this.params?.platform) {
      this.storageService.addSessionData('platform', this.params?.platform);
    }
    const sesPlatform = this.storageService.getFromSession('platform');
    if (sesPlatform) {
      this.variablesService.deviceType = sesPlatform;
    }
  }

  /**Onlaod function */
  @HostListener('window:load', [])
  onWindowLoads() {
    if (this.utilService.isBrowser) {
      this.scriptLoaderService.loadScript('gtm', '', true).then();
      this.scriptLoaderService.loadScript('clevertap', '', true);
      this.scriptLoaderService.loadScript('fb_pixel_script', '', true);
      this.scriptLoaderService.loadScript('microsoft_clarity', '', true);
      // this.scriptLoaderService.load('google_optimize').then(() => {
      //   const _ct = new CustomEvent('google_optimize', { bubbles: true });
      //   document.dispatchEvent(_ct);
      // });
      if (!this.router.url.match('stories|fundraiser|thankyou|pledge-more|sip|portfolio|payment')) {
        this.events.pushNotificationCL();
      }
      this.variablesService.userData.subscribe(res => {
        if (res?.id) {
          this.events.clarityEventsPush(res?.id);
        }
      })
    }
  }

  routeChangeListner() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) { route = route.firstChild; }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data),
    ).subscribe((event) => {
      if (this.router.url.split('?')[0] !== this.variablesService.previousPageURL.split('?')[0]) {
        this.variablesService.previousPageURL = this.router.url;
        this.variablesService.pageName = event['page_name'] || '';
        this.utilService.setUtm();
        this.events.fbPixelPush('PageView', {});
        this.gtmOnLoad(event);
        if (!event.lastOrderSkip) {
          this.checkForLastOrder();
        }
      }
    });
  }

  /**Set user logged in flags */
  setUser() {
    try {
      const user = this.utilService.getUser()?.user;
      if (user && !this.variablesService.isVariableLogin) {
        if (user.isLoggedIn) {
          this.utilService.isLoggedIn.next(user.isLoggedIn);
        }
        this.variablesService.userData.next(this.utilService.getUserData());
        const domain = environment.name === 'local' ? 'localhost' : '.ketto.org';
        this.cookie.setCookie('is_logged_in', 'true', 365, domain);
      }
    } catch (error) {
      console.log(error);
    }
  }
  /**Lazy loader module loader */
  moduleLoader() {
    this.router.events.subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
        this.loadingRouteConfig = true;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.loadingRouteConfig = false;
      }
    });
  }
  /**
   * Set page title on the basis of which
   * route is currently active
   */
  setTitle() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.utilService.url.next(event);
      try {
        this.seoService.createCanonicalURL();
      } catch (e) {
        console.log(e);
      }
    });
  }
  /**Get the IP of the client */
  getClientIP() {
    const ip = this.storageService.getFromSession('iplocation');
    if (!ip) {
      this.authService.getClientIP().subscribe((res: any) => {
        this.setClientIP(res.data);
      }, () => this.setClientIP(this.variablesService.defaultIPLocation));
    } else {
      this.setClientIP(ip);
    }
  }

  setClientIP(ip: ICLientData) {
    this.variablesService.clientLocationData.next(ip);
    const criteoId = this.variablesService.criteoPartnerIDs[ip.country_name] || 66025;
    this.events.gtmPush({ 'CriteoPartnerID': criteoId });
    this.storageService.checkFromSession('iplocation', ip);
    this.setCurrency(ip);
  }

  setCurrency(ip: ICLientData) {
    const currencyFromUrl = this.activatedRoute.snapshot.queryParams.selected_currency;
    const currencyFromCode = this.utilService.getCurrencyFromCode(ip.country_code);
    const currencyFromSession = this.storageService.getFromSession('currency');
    if (currencyFromUrl) {
      this.utilService.setCurrency(currencyFromUrl);
    } else if (currencyFromSession) {
      this.utilService.setCurrency(currencyFromSession);
    } else if (currencyFromCode) {
      this.utilService.setCurrency(currencyFromCode.currency);
    }
  }

  gtmOnLoad(event) {
    const user: IUser = this.utilService.getUserData();
    const currency = this.storageService.getFromSession('currency');
    const gtmPush = {
      event: 'virtual_pageview',
      page_version: 'A',
      login_status: user ? 'logged in' : 'not logged in', // Populate either LOGGED IN or NOT LOGGED IN
      user_type: user ? user.entity_type : 'visitor',
      currency: currency, // Currency used
      // session_id: '',
      user_id: user ? user.id : '',   // Identifies unique users across multiple devices
      page_category: event.category,  // ex: home, crowdfunding, fundraiser etc
      page_name: event.page_name,  // name of the page
      sip: event.sip || false  // name of the page
    };
    this.variablesService.gtmPageData = gtmPush;
    this.events.gtmPush(gtmPush);
  }

  /**Get the total orders made by the user */
  checkForLastOrder() {
    this.variablesService.userData.subscribe(res => {
      if (res && !this.variablesService.lastOrderInfo.value) {
        this.getLastOrder();
      }
    });
  }
  getLastOrder() {
    const qparam = `?limit=1&orderBy=creation_date&sortedBy=desc`;
    this.portfolioService.getBeneficiaries(qparam).subscribe((res: any) => {
      if (res && res.data && res.data.data && res.data.data.length) {
        this.variablesService.setLastOrderValues(res.data.data[0]);
      }
    });
  }

}
